import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * WealthFirst Assistant — instant-reply concierge chat bubble.
 * - Pre-defined intents reply INSTANTLY (no LLM round-trip)
 * - Free-form text is sent to /api/assistant/chat (Claude Haiku 4.5)
 */

// ─────────────────────────────────────────────────────────────────────────────
// Intent knowledge base — instant canned replies for the four main CTAs and
// the most common deep-dive questions. Each reply is a list of message blocks:
//   { kind: "text" | "link", text, to?, label? }
// ─────────────────────────────────────────────────────────────────────────────
const INTENTS = {
  view_ai_report: {
    label: "View AI Report",
    chips: ["What is the AI Funding Report?", "How long does it take?"],
    reply: [
      { kind: "text", text: "We run two AI reports. (1) Business Credit Readiness Diagnostic — free; shows your underwriter-facing profile, an approval score, fixable compliance gaps, and a 90-day roadmap to position for $80K–$350K in credit lines. (2) AI Funding Report — $139; compliance-grade regulatory readiness for Reg-D raises (506(b)/506(c))." },
      { kind: "link", to: "/credit-readiness", label: "Start with the FREE Credit Readiness Diagnostic" },
      { kind: "link", to: "/funding/apply?plan=ai_report", label: "Get the $139 Funding Report" },
    ],
  },
  autopilot: {
    label: "AutoPilot",
    chips: ["What is AutoPilot?", "Is there a trial?"],
    reply: [
      { kind: "text", text: "WealthFirst AutoPilot is our hands-off, autonomous funding pilot — our team + AI runs your raise for you: investor outreach sequencing, data-room curation, DD prep, and weekly investor updates." },
      { kind: "text", text: "$91 / month with a 7-day trial. Think of it as a fractional funding-ops team on tap." },
      { kind: "link", to: "/funding/apply?plan=autopilot", label: "Start the 7-day trial" },
    ],
  },
  contact: {
    label: "Contact Us",
    chips: ["Talk to a consultant", "What's the editorial desk email?"],
    reply: [
      { kind: "text", text: "Reach our consulting desk anytime at support@wealthfirstinc.tv — a partner advisor replies within one business day." },
      { kind: "link", to: "/contact", label: "Open the contact form" },
    ],
  },
  newsletter: {
    label: "Monthly Briefing",
    chips: ["What is the briefing?", "How do I subscribe?"],
    reply: [
      { kind: "text", text: "The WealthFirst Private Intelligence briefing is our automated monthly editorial for UHNW earners, family offices, advisors, and luxury-market partners." },
      { kind: "text", text: "Five editorial pillars — Wealth & Family · Market & Capital · Luxury & Assets · Policy & Tax · Deal Flow. Three editions: Public (free Cover Brief teaser), Premium (full briefing), and Advisor / Family-Office (private notes)." },
      { kind: "link", to: "/newsletter", label: "Access the Briefing" },
    ],
  },
  request_consultation: {
    label: "Request a Consultation",
    chips: ["Talk to an advisor", "Submit my details"],
    reply: [
      { kind: "text", text: "Share four quick details (name · email · company · message) and a partner advisor will reach out within one business day." },
      { kind: "link", to: "/lead", label: "Open the Lead Form" },
    ],
  },
  take_quiz: {
    label: "Take Quiz",
    chips: ["What is the right plan for me?"],
    reply: [
      { kind: "text", text: "Quick 4-question quiz to match you with the right funding tier. Answer the chips as they appear below." },
      { kind: "quiz_start" },
    ],
  },
  pricing: {
    label: "Pricing per tier",
    chips: [],
    reply: [
      { kind: "text", text: "Funding plans:\n• AI Funding Report — $139 one-time\n• WealthFirst PRO — $45/mo or $365/yr\n• WealthFirst AutoPilot — $91/mo (7-day trial)" },
      { kind: "text", text: "White-label Franchise tiers:\n• Starter — $2,400 setup\n• Grow-Rich — $9,000 upfront, 4×$2,250, or $9,999 list\n• Enterprise — $24,000 setup" },
      { kind: "link", to: "/franchise/compare", label: "Compare franchise tiers" },
    ],
  },
  ai_reports_work: {
    label: "How do AI Reports work?",
    chips: [],
    reply: [
      { kind: "text", text: "You answer ~10 questions about your raise (entity, jurisdiction, structure, target investors). Within minutes Claude Haiku 4.5 produces a compliance-grade HTML report — sanitized with DOMPurify and displayed in your account." },
      { kind: "text", text: "It is built to be reviewable by securities counsel and immediately actionable — not a marketing fluff piece." },
      { kind: "link", to: "/funding/apply?plan=ai_report", label: "Run my report" },
    ],
  },
  what_is_autopilot: {
    label: "What is Autonomous Pilot / AutoPilot?",
    chips: [],
    reply: [
      { kind: "text", text: "Autonomous Pilot (AutoPilot) is end-to-end raise execution. We sequence investor outreach, manage your data room, prep DD documents, and ship weekly investor updates — using AI for scale and humans for judgment calls." },
      { kind: "text", text: "$91 / month with a 7-day trial. You stay focused on building; we run the funnel." },
      { kind: "link", to: "/funding/apply?plan=autopilot", label: "Start AutoPilot" },
    ],
  },
};

const QUIZ = [
  {
    q: "How much capital do you intend to raise?",
    options: [
      { label: "<$500K", weight: { ai_report: 3, pro: 2 } },
      { label: "$500K–$5M", weight: { pro: 3, autopilot: 2 } },
      { label: "$5M+", weight: { autopilot: 3, franchise: 1 } },
    ],
  },
  {
    q: "How much time can you personally spend on the raise?",
    options: [
      { label: "Full-time", weight: { ai_report: 3, pro: 2 } },
      { label: "A few hours / week", weight: { pro: 3 } },
      { label: "Almost none", weight: { autopilot: 4 } },
    ],
  },
  {
    q: "Do you have a securities attorney on retainer?",
    options: [
      { label: "Yes", weight: { ai_report: 3, pro: 2 } },
      { label: "Not yet", weight: { pro: 2, autopilot: 2 } },
    ],
  },
  {
    q: "What is your priority?",
    options: [
      { label: "Compliance posture", weight: { ai_report: 4 } },
      { label: "Steady deal flow", weight: { pro: 4 } },
      { label: "Hands-off execution", weight: { autopilot: 4 } },
      { label: "Launching my own brand", weight: { franchise: 4 } },
    ],
  },
];

const QUIZ_RESULT = {
  ai_report: {
    title: "AI Funding Report — $139",
    body: "Best fit. Run the report, hand it to your counsel, and execute.",
    to: "/funding/apply?plan=ai_report",
    cta: "Get my AI report",
  },
  pro: {
    title: "WealthFirst PRO — $45 / mo",
    body: "Best fit. Weekly office hours + curated accredited-investor introductions.",
    to: "/funding/apply?plan=pro_monthly",
    cta: "Activate PRO",
  },
  autopilot: {
    title: "WealthFirst AutoPilot — $91 / mo",
    body: "Best fit. We run the raise; you focus on the business. 7-day trial.",
    to: "/funding/apply?plan=autopilot",
    cta: "Start AutoPilot",
  },
  franchise: {
    title: "White-Label Franchise — from $2,400",
    body: "Best fit. Co-branded funding tech, lead routing, and a partner-success advisor.",
    to: "/franchise",
    cta: "Compare franchise tiers",
  },
};

const PRIMARY_CHIPS = [
  { id: "view_ai_report", label: "View AI Report" },
  { id: "autopilot", label: "AutoPilot" },
  { id: "newsletter", label: "Monthly Briefing" },
  { id: "request_consultation", label: "Request Consultation" },
  { id: "contact", label: "Contact Us" },
  { id: "take_quiz", label: "Take Quiz" },
];

const SECONDARY_CHIPS = [
  { id: "pricing", label: "What is the pricing per Tiers?" },
  { id: "ai_reports_work", label: "How do AI Reports work?" },
  { id: "what_is_autopilot", label: "What is AutoPilot?" },
];

const GREETING = {
  role: "assistant",
  blocks: [
    { kind: "text", text: "Hi I'm WealthFirst's assistant. How can I help?" },
  ],
};

function makeUserMessage(text) {
  return { role: "user", blocks: [{ kind: "text", text }] };
}

function makeAssistantMessage(blocks) {
  return { role: "assistant", blocks };
}

export default function AssistantBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `s-${Math.random().toString(36).slice(2, 10)}`);
  const [quizState, setQuizState] = useState({ active: false, step: 0, scores: {} });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, quizState]);

  const sendIntent = (id) => {
    const intent = INTENTS[id];
    if (!intent) return;
    setMessages((m) => [
      ...m,
      makeUserMessage(intent.label),
      makeAssistantMessage(intent.reply),
    ]);
    if (id === "take_quiz") {
      setQuizState({ active: true, step: 0, scores: {} });
    }
  };

  const answerQuiz = (optionIdx) => {
    const step = quizState.step;
    const option = QUIZ[step].options[optionIdx];
    const nextScores = { ...quizState.scores };
    for (const [k, v] of Object.entries(option.weight)) {
      nextScores[k] = (nextScores[k] || 0) + v;
    }
    setMessages((m) => [...m, makeUserMessage(option.label)]);

    if (step + 1 < QUIZ.length) {
      setQuizState({ active: true, step: step + 1, scores: nextScores });
      return;
    }

    // Compute winner
    const winner = Object.entries(nextScores).sort((a, b) => b[1] - a[1])[0]?.[0] || "pro";
    const result = QUIZ_RESULT[winner];
    setMessages((m) => [
      ...m,
      makeAssistantMessage([
        { kind: "text", text: `Based on your answers, your best fit is:` },
        { kind: "text", text: `${result.title} — ${result.body}` },
        { kind: "link", to: result.to, label: result.cta },
      ]),
    ]);
    setQuizState({ active: false, step: 0, scores: {} });
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, makeUserMessage(text)]);
    setBusy(true);

    // Build conversational history for the LLM (text-only)
    const history = [...messages, makeUserMessage(text)]
      .filter((m) => m.role === "user")
      .map((m) => ({ text: m.blocks.map((b) => b.text || "").join(" ").slice(0, 1000) }));

    try {
      const { data } = await api.post("/assistant/chat", {
        messages: history.slice(-10),
        session_id: sessionId,
      });
      setMessages((m) => [...m, makeAssistantMessage([{ kind: "text", text: data.reply }])]);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Try again in a moment.";
      setMessages((m) => [
        ...m,
        makeAssistantMessage([
          { kind: "text", text: detail },
          { kind: "text", text: "Or reach our consultant: support@wealthfirstinc.tv" },
        ]),
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="assistant-bubble-toggle"
        aria-label={open ? "Close WealthFirst Assistant" : "Open WealthFirst Assistant"}
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: 60,
          height: 60,
          borderRadius: "50%",
          background: "var(--brass)",
          color: "var(--ink)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 10px 30px rgba(0,0,0,0.35), 0 0 0 1px rgba(194,163,115,0.4)",
          zIndex: 9998,
          border: "none",
          cursor: "pointer",
          transition: "transform 200ms ease",
          transform: open ? "scale(0.95)" : "scale(1)",
        }}
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="assistant-bubble-panel"
          style={{
            position: "fixed",
            bottom: 96,
            right: 24,
            width: "min(380px, calc(100vw - 32px))",
            maxHeight: "min(600px, calc(100vh - 140px))",
            background: "var(--surface)",
            border: "1px solid var(--brass)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            display: "flex",
            flexDirection: "column",
            zIndex: 9998,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "16px 18px",
              borderBottom: "1px solid var(--hairline)",
              background: "var(--ink)",
              color: "var(--ink-light)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Sparkles size={16} style={{ color: "var(--brass)" }} />
            <div style={{ flex: 1 }}>
              <p
                style={{
                  fontSize: 10,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "var(--brass)",
                  margin: 0,
                }}
              >
                WealthFirst Assistant
              </p>
              <p style={{ fontSize: 12, margin: 0, color: "var(--ink-light)", opacity: 0.7 }}>
                Replies instantly · powered by AI
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="assistant-close"
              aria-label="Close assistant"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--ink-light)",
                cursor: "pointer",
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="assistant-messages"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "16px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              background: "var(--surface)",
            }}
          >
            {messages.map((m, idx) => (
              <MessageRow key={idx} message={m} />
            ))}

            {/* Quick chips — primary intents (shown after greeting) */}
            {!quizState.active && messages.length <= 1 && (
              <div
                data-testid="assistant-primary-chips"
                style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}
              >
                {PRIMARY_CHIPS.map((c) => (
                  <Chip
                    key={c.id}
                    label={c.label}
                    onClick={() => sendIntent(c.id)}
                    testid={`assistant-chip-${c.id}`}
                  />
                ))}
              </div>
            )}

            {/* Secondary chips — common questions */}
            {!quizState.active && messages.length <= 1 && (
              <div
                data-testid="assistant-secondary-chips"
                style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}
              >
                {SECONDARY_CHIPS.map((c) => (
                  <Chip
                    key={c.id}
                    label={c.label}
                    full
                    onClick={() => sendIntent(c.id)}
                    testid={`assistant-chip-${c.id}`}
                  />
                ))}
              </div>
            )}

            {/* Quiz step */}
            {quizState.active && (
              <div data-testid={`assistant-quiz-step-${quizState.step}`}>
                <MessageRow
                  message={makeAssistantMessage([{ kind: "text", text: QUIZ[quizState.step].q }])}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                  {QUIZ[quizState.step].options.map((opt, i) => (
                    <Chip
                      key={i}
                      label={opt.label}
                      full
                      onClick={() => answerQuiz(i)}
                      testid={`assistant-quiz-option-${quizState.step}-${i}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {busy && (
              <div
                data-testid="assistant-typing"
                style={{
                  alignSelf: "flex-start",
                  fontSize: 12,
                  color: "var(--ink-muted)",
                  fontStyle: "italic",
                  padding: "4px 8px",
                }}
              >
                Assistant is typing…
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={send}
            style={{
              borderTop: "1px solid var(--hairline)",
              padding: 10,
              display: "flex",
              gap: 8,
              background: "var(--surface)",
            }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything — pricing, plans, partnership…"
              data-testid="assistant-input"
              style={{
                flex: 1,
                background: "transparent",
                border: "1px solid var(--hairline-strong)",
                padding: "10px 12px",
                fontSize: 14,
                color: "var(--ink)",
                outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={!input.trim() || busy}
              data-testid="assistant-send"
              aria-label="Send"
              style={{
                background: "var(--brass)",
                color: "var(--ink)",
                border: "none",
                padding: "10px 12px",
                cursor: input.trim() && !busy ? "pointer" : "not-allowed",
                opacity: input.trim() && !busy ? 1 : 0.5,
              }}
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function MessageRow({ message }) {
  const isUser = message.role === "user";
  return (
    <div
      style={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "85%",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      {message.blocks.map((b, i) => {
        if (b.kind === "link" && b.to) {
          return (
            <Link
              key={i}
              to={b.to}
              data-testid="assistant-message-link"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "var(--brass)",
                color: "var(--ink)",
                padding: "8px 12px",
                fontSize: 12,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontWeight: 600,
                width: "fit-content",
                textDecoration: "none",
              }}
            >
              {b.label}
              <ArrowRight size={12} />
            </Link>
          );
        }
        return (
          <div
            key={i}
            style={{
              background: isUser ? "var(--brass)" : "rgba(194,163,115,0.08)",
              color: isUser ? "var(--ink)" : "var(--ink)",
              padding: "10px 14px",
              fontSize: 13.5,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              border: isUser ? "none" : "1px solid var(--hairline)",
            }}
          >
            {b.text}
          </div>
        );
      })}
    </div>
  );
}

function Chip({ label, onClick, full, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        background: "transparent",
        border: "1px solid var(--brass)",
        color: "var(--brass)",
        padding: "8px 12px",
        fontSize: 12,
        letterSpacing: "0.05em",
        cursor: "pointer",
        textAlign: "left",
        width: full ? "100%" : "auto",
        transition: "background 150ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(194,163,115,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {label}
    </button>
  );
}
